// -----------------------------------------------------------------------------
// ConfirmationPanel  (CONFIRMATION)
//
// Mirrors ConfirmationCell in TaskChecklistField.jsx. Shows the question and,
// when the item requires a yes/no answer, Yes / No buttons; otherwise a single
// "Mark complete". Does the /complete/ POST itself (with response_payload), then
// calls onComplete(item) purely as a refresh signal — same self-contained pattern
// as UploadDocumentPanel. The orchestrator's follow-on /complete/ is idempotent
// and (verified) does NOT clobber the response_payload written here.
//
// Endpoint (mirrors ConfirmationCell.handleAnswer → useStatusApi.postState):
//   POST /api/v1/practices/{practiceId}/tasks/{taskId}/checklist/{itemId}/complete/
//   body { completion_state: 'YES', response_payload: { answer } }
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Typography, Button, Stack, CircularProgress } from '@mui/material';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import Iconify from 'src/components/Iconify';

export default function ConfirmationPanel({ item, practiceId, onComplete, onClose }) {
    const [submitting, setSubmitting] = useState(false);

    // requires_confirmation drives the yes/no vs single-button presentation,
    // mirroring ConfirmationCell (reads action_config.requires_confirmation).
    const actionConfig = item?.raw?.action_config || item?.action_config || {};
    const requiresConfirmation = !!actionConfig.requires_confirmation;
    const title = item?.title || item?.name || item?.description || 'Did you complete this?';

    const taskId = item?.taskId ?? item?.rawTask?.id;
    const itemId = item?.itemId ?? item?.id;

    const submit = async (answer) => {
        if (submitting) return;
        if (!practiceId || !taskId || !itemId) {
            // No real task yet (e.g. preview) — just signal the orchestrator.
            onComplete?.(item);
            return;
        }
        setSubmitting(true);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/complete/`,
                'POST',
                { completion_state: 'YES', response_payload: { answer } }
            );
            if (res.ok) {
                onComplete?.(item);
            } else {
                dispatch(updateError({ success: false, message: res?.data?.detail || 'Failed to confirm — please try again.' }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err?.message || 'Failed to confirm — please try again.' }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            data-testid="confirmation-panel"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                px: 3,
                py: 2.5,
                gap: 1.75,
                textAlign: 'center',
            }}
        >
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '14px',
                    bgcolor: 'rgba(0,167,111,0.10)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Iconify icon="eva:checkmark-circle-2-outline" width={26} height={26} sx={{ color: '#00A76F' }} />
            </Box>

            <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                    Confirm
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#6e6e73', mt: 0.5, px: 1 }}>
                    {title}
                </Typography>
            </Box>

            {requiresConfirmation ? (
                <Stack direction="row" spacing={1} sx={{ width: '100%', maxWidth: 280 }}>
                    <Button
                        data-testid="confirmation-panel-no"
                        variant="outlined"
                        fullWidth
                        disabled={submitting}
                        onClick={() => submit('no')}
                        sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600 }}
                    >
                        No
                    </Button>
                    <Button
                        data-testid="confirmation-panel-yes"
                        variant="contained"
                        disableElevation
                        fullWidth
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
                        onClick={() => submit('yes')}
                        sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, bgcolor: '#00A76F', '&:hover': { bgcolor: '#007867' }, boxShadow: 'none' }}
                    >
                        Yes, confirm
                    </Button>
                </Stack>
            ) : (
                <Button
                    data-testid="confirmation-panel-complete"
                    variant="contained"
                    disableElevation
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:check-circle-outline" width={16} height={16} />}
                    onClick={() => submit('yes')}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, bgcolor: '#00A76F', '&:hover': { bgcolor: '#007867' }, boxShadow: 'none' }}
                >
                    Mark complete
                </Button>
            )}

            <Button
                variant="text"
                size="small"
                disabled={submitting}
                onClick={onClose}
                sx={{ textTransform: 'none', fontSize: '13px', color: '#6e6e73' }}
            >
                Cancel
            </Button>
        </Box>
    );
}

ConfirmationPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

ConfirmationPanel.defaultProps = {
    item: null,
    practiceId: null,
    onComplete: null,
};
